// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import BackendAuthHandler from 'BackendAuthHandler';
import {
  RecentlyViewedFilters,
  RecentlyViewedListResponse,
  RecentlyViewedResponse,
} from 'RecentlyViewed';
import {
  ActivityLog,
  ActivityLogModelType,
  ActivityLogResponse,
  ActivityLogWithDescriptions,
} from '../../models/ActivityLog';
export const adminBackendServiceActivityMixin = {
  async getActivityLogEntry(activityLogId: string): Promise<ActivityLog> {
    console.log('BackendService: getActivityLogEntry called with ID:', activityLogId);
    return this.authFetchPromise(async (token) => {
      const url = `/api/activityLog/${activityLogId}`;
      console.log('BackendService: Making request to:', url);
      const response = await this.authenticatedFetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (await this.handleAuthRedirect(response)) {
        return undefined as unknown as ActivityLog;
      }
      if (response.status === 404) {
        throw 'Activity log entry not found';
      }
      if (!response.ok) {
        throw 'Unable to fetch activity log entry';
      }
      const data = (await response.json()) as { data: ActivityLog };
      return data.data;
    });
  },

  async getPushReceiptStatus(
    actionLogId: string,
    persist?: boolean,
  ): Promise<{
    data: {
      pushTicketIds: string[];
      receipts: Record<string, { status: string; message?: string; details?: { error?: string } }>;
      persisted?: boolean;
    };
  }> {
    const token = await new BackendAuthHandler().withAuthToken();
    const url = `/api/action-event-logs/${encodeURIComponent(actionLogId)}/push-receipt-status${persist ? '?persist=true' : ''}`;
    const response = await this.authenticatedFetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `Failed to fetch push receipt status: ${response.status}`);
    }
    return response.json();
  },

  getActivityLogs(
    modelType: ActivityLogModelType,
    modelId: string,
    page: number = 1,
    limit: number = 50,
    filters?: {
      eventType?: string;
      associatedModelType?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<ActivityLogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
    }

    return this.authApiGetAuthed(
      `/api/models/${modelType}/${modelId}/activity-logs?${params.toString()}`,
      'Unable to retrieve activity logs',
      (json) => json as ActivityLogResponse,
      true,
    );
  },

  getActivityLogsByLinkedModel(
    linkedModelType: ActivityLogModelType,
    linkedModelExternalId: string,
    limit: number = 50,
  ): Promise<ActivityLogWithDescriptions[]> {
    return this.authApiGetAuthed(
      `/api/action-event-logs/by-linked-model/${linkedModelType}/${linkedModelExternalId}?limit=${limit}`,
      'Unable to retrieve activity logs by linked model',
      (json) => json.data || [],
      true,
    );
  },

  getAllActivityLogs(
    page: number = 1,
    limit: number = 50,
    filters?: {
      eventType?: string | string[];
      initiatedByType?: string;
      initiatedByExternalId?: string | string[];
      associatedModelType?: string | string[];
      associatedModelExternalId?: string | string[];
      linkedModelType?: string | string[];
      linkedModelExternalId?: string | string[];
      startDate?: string;
      endDate?: string;
    },
  ): Promise<ActivityLogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.initiatedByType) {
        params.append('initiatedByType', filters.initiatedByType);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const arrayFields = [
        'eventType',
        'initiatedByExternalId',
        'associatedModelType',
        'associatedModelExternalId',
        'linkedModelType',
        'linkedModelExternalId',
      ] as const;

      arrayFields.forEach((field) => {
        const value = filters[field];
        if (value) {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(`${field}[]`, v));
          } else {
            params.append(field, value);
          }
        }
      });
    }

    return this.authApiGetAuthed(
      `/api/action-event-logs?${params.toString()}`,
      'Unable to retrieve all activity logs',
      (json) => json as ActivityLogResponse,
      true,
    );
  },

  registerModelView(modelType: string, modelId: string): Promise<RecentlyViewedResponse> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch('/api/recently-viewed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ modelType, modelId }),
      });
      if (response.ok) {
        return response.json() as Promise<RecentlyViewedResponse>;
      }
      if (response.status === 400) {
        throw await response.json();
      }
      throw 'Failed to register model view';
    });
  },

  getRecentlyViewed(filters?: RecentlyViewedFilters): Promise<RecentlyViewedListResponse> {
    const params = new URLSearchParams();
    if (filters?.modelType) {
      params.append('modelType', filters.modelType);
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    const query = params.toString();
    return this.authApiGetAuthed(
      `/api/recently-viewed${query ? `?${query}` : ''}`,
      'Failed to fetch recently viewed items',
      (json) => json as RecentlyViewedListResponse,
      true,
    );
  },
} satisfies ThisType<BaseBackendService>;
